import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { invalidateAvatarCache } from "@/hooks/useAvatar";
import { useSubscription } from "@/hooks/useSubscription";

// 'avaturn' retained for backwards-compat with legacy library rows.
// Sub-task B bulk-migrated active rows; soft-archived rows may still
// surface 'avaturn'. Post-W4 no new insert produces 'avaturn'.
export type AvatarProvider = "avaturn" | "metaperson";

export interface UserAvatar {
  id: string;
  user_id: string;
  avatar_url: string;
  provider: AvatarProvider;
  label: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const FREE_AVATAR_LIMIT = 3;

interface UseAvatarLibraryApi {
  avatars: UserAvatar[];
  activeAvatar: UserAvatar | null;
  count: number;
  limit: number;
  canAddAvatar: boolean;
  isPremium: boolean;
  addAvatar: (
    url: string,
    provider: AvatarProvider,
    label?: string,
  ) => Promise<UserAvatar | null>;
  setActive: (avatarId: string) => Promise<void>;
  renameAvatar: (avatarId: string, label: string) => Promise<void>;
  deleteAvatar: (avatarId: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

async function fetchPlan(userId: string): Promise<string> {
  try {
    const { data } = await supabase
      .from("user_profiles")
      .select("plan")
      .eq("user_id", userId)
      .maybeSingle();
    const p = data as Record<string, unknown> | null;
    return (p?.plan as string) || "free";
  } catch {
    return "free";
  }
}

async function fetchAvatars(userId: string): Promise<UserAvatar[]> {
  const { data, error } = await (supabase as any)
    .from("user_avatars")
    .select("id, user_id, avatar_url, provider, label, is_active, created_at, updated_at")
    .eq("user_id", userId)
    .order("is_active", { ascending: false })
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data as UserAvatar[]) || [];
}

export function useAvatarLibrary(): UseAvatarLibraryApi {
  const [avatars, setAvatars] = useState<UserAvatar[]>([]);
  const [plan, setPlan] = useState<string>("free");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Canonical premium check: requires plan in {premium,pro} AND status active.
  // Replaces the previous loose `plan !== "free"` which ignored status and
  // diverged from the rest of the app (past_due / canceled were granted access).
  const { isPremium } = useSubscription();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setAvatars([]);
        setPlan("free");
        return;
      }
      const [rows, userPlan] = await Promise.all([
        fetchAvatars(user.id),
        fetchPlan(user.id),
      ]);
      setAvatars(rows);
      setPlan(userPlan);
    } catch (err) {
      console.error("[useAvatarLibrary] load failed:", err);
      setError("Failed to load avatars");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const limit = isPremium ? Number.POSITIVE_INFINITY : FREE_AVATAR_LIMIT;
  const count = avatars.length;
  const canAddAvatar = count < limit;
  const activeAvatar = avatars.find((a) => a.is_active) || null;

  const addAvatar = useCallback(
    async (
      url: string,
      provider: AvatarProvider,
      label?: string,
    ): Promise<UserAvatar | null> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const currentPlan = await fetchPlan(user.id);
      const currentLimit =
        currentPlan !== "free" ? Number.POSITIVE_INFINITY : FREE_AVATAR_LIMIT;
      const current = await fetchAvatars(user.id);
      if (current.length >= currentLimit) return null;

      const { data, error: insertErr } = await (supabase as any)
        .from("user_avatars")
        .insert({
          user_id: user.id,
          avatar_url: url,
          provider,
          is_active: true,
          label: label ?? null,
        })
        .select("id, user_id, avatar_url, provider, label, is_active, created_at, updated_at")
        .single();

      if (insertErr) throw insertErr;

      invalidateAvatarCache();
      await load();
      return data as UserAvatar;
    },
    [load],
  );

  const setActive = useCallback(
    async (avatarId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error: updErr } = await (supabase as any)
        .from("user_avatars")
        .update({ is_active: true })
        .eq("id", avatarId)
        .eq("user_id", user.id);

      if (updErr) throw updErr;

      invalidateAvatarCache();
      await load();
    },
    [load],
  );

  const renameAvatar = useCallback(
    async (avatarId: string, label: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const trimmed = label.trim();
      const { error: updErr } = await (supabase as any)
        .from("user_avatars")
        .update({ label: trimmed || null })
        .eq("id", avatarId)
        .eq("user_id", user.id);

      if (updErr) throw updErr;

      await load();
    },
    [load],
  );

  const deleteAvatar = useCallback(
    async (avatarId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error: delErr } = await (supabase as any)
        .from("user_avatars")
        .delete()
        .eq("id", avatarId)
        .eq("user_id", user.id);

      if (delErr) throw delErr;

      // The DB trigger (sync_active_avatar_to_profile, AFTER DELETE) clears
      // user_profiles.avatar_url + avatar_provider when the deleted row was
      // active, but leaves avatar_source / avatar_type / avatar_snapshot_url
      // orphaned — useAvatarSnapshot path 2 would then return a STALE snapshot
      // for an avatar that no longer exists (edit-profile spinner / broken img).
      // If no active avatar remains, reset user_profiles to the canonical
      // "no avatar" state across ALL fields so consumers fall back to the
      // portal instead of rendering an orphaned GLB/snapshot.
      const { data: remainingActive } = await (supabase as any)
        .from("user_avatars")
        .select("id")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .maybeSingle();

      if (!remainingActive) {
        const { error: resetErr } = await (supabase as any)
          .from("user_profiles")
          .update({
            avatar_url: null,
            avatar_provider: "none",
            avatar_source: null,
            avatar_type: "placeholder",
            avatar_snapshot_url: null,
          })
          .eq("user_id", user.id);
        if (resetErr) {
          // Surface but don't block — the trigger already cleared the two
          // critical fields; this is the belt-and-suspenders sweep.
          console.error("[useAvatarLibrary] profile avatar reset failed:", resetErr);
        }
      }

      invalidateAvatarCache();
      await load();
    },
    [load],
  );

  return {
    avatars,
    activeAvatar,
    count,
    limit,
    canAddAvatar,
    isPremium,
    addAvatar,
    setActive,
    renameAvatar,
    deleteAvatar,
    loading,
    error,
    refetch: load,
  };
}
